import {
  AdminRole,
  KeycloakIdStorageKey,
  readSessionTokenWithMigration,
  clearSessionStorageKeys,
  TeacherRole,
} from "./Config";
import { GetJwtTokenAsJson, type KeycloakTokenPayload } from "./Jwt";

/**
 * Repris du portail `frontend/src/model/Functions.ts`, en s’appuyant sur le
 * `localStorage` (jeton + `keycloakID`) : pas de cookies, `getCurrentUser` =
 * jeton de session présent et utilisable.
 */
export const getCurrentUser = () => {
  return isAuthenticated();
};

export const isAdminUser = () => {
  const jwt = GetJwtTokenAsJson();
  if (getCurrentUser() && jwt) {
    const roles = jwt.realm_access?.roles;
    if (roles == null) return false;
    return roles.filter((elt) => elt === AdminRole).length > 0;
  }
  return false;
};

export const GetCurrentUserUsername = () => {
  const jwt = GetJwtTokenAsJson();
  if (jwt?.preferred_username) {
    return jwt.preferred_username;
  }
  return "";
};

export const ClearSession = () => {
  clearSessionStorageKeys();
};

/**
 * Comme le portail : le rôle enseignant seul (l’admin est géré par `isAdminUser`).
 * Le backend doc autorise en pratique profs + admins sur certaines routes.
 */
export const isTeacherUser = () => {
  const jwt = GetJwtTokenAsJson();
  if (getCurrentUser() && jwt) {
    const roles = jwt.realm_access?.roles;
    if (roles == null) return false;
    return roles.filter((elt) => elt === TeacherRole).length > 0;
  }
  return false;
};

/**
 * Gestion d’une fiche (masquer, modifier, supprimer, resynchroniser) : admin toujours,
 * enseignant uniquement s’il en est le créateur (aligné sur le backend
 * `assertCanManageDocumentation`).
 */
export const canManageDoc = (createdBy?: string | null): boolean => {
  if (isAdminUser()) {
    return true;
  }
  if (!isTeacherUser()) {
    return false;
  }
  const sub = GetJwtTokenAsJson()?.sub;
  return Boolean(sub && createdBy && createdBy === sub);
};

export const ParseToken = (token: string) => {
  if (token === "") {
    return;
  }
  const authObject = GetJwtTokenAsJson(token) as KeycloakTokenPayload | null;
  if (authObject?.sub) {
    localStorage.setItem(KeycloakIdStorageKey, authObject.sub);
  }
};

export { GetJwtTokenAsJson } from "./Jwt";
export type { KeycloakTokenPayload } from "./Jwt";

export const GetStoredJwtToken = () => {
  return readSessionTokenWithMigration();
};

export const isAuthenticated = (): boolean => {
  const token = GetStoredJwtToken();
  if (!token) return false;
  try {
    const jwt = GetJwtTokenAsJson(token);
    return jwt != null;
  } catch {
    return false;
  }
};
